<template>
  <v-dialog
    :value="video.state.open"
    max-width="760px"
    scrollable
    :persistent="video.busy.value"
    @input="$event || video.close()"
  >
    <v-card v-if="item" class="gw-video">
      <v-card-title class="gw-video__head">
        <div class="gw-video__eyebrow">
          Étape 2 · {{ platformLabel }}
          <span v-if="durationLabel"> · {{ durationLabel }}</span>
        </div>
        <div class="gw-video__title" :title="item.title">{{ item.title }}</div>
      </v-card-title>

      <v-card-text class="gw-video__body">
        <!-- On affiche la VIDÉO, pas son texte : la description d'une source
             vidéo est sa légende RSS, elle ne dit rien de ce qui est dit. -->
        <div class="gw-video__player">
          <iframe
            v-if="embedUrl"
            :src="embedUrl"
            class="gw-video__frame"
            allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture"
            allowfullscreen
            title="Aperçu de la vidéo"
          ></iframe>
          <!-- Hors YouTube, l'embed est reporté : miniature et lien suffisent
               à reconnaître la vidéo qu'on est en train de traiter. -->
          <a
            v-else
            :href="item.url"
            target="_blank"
            rel="noopener"
            class="gw-video__poster"
          >
            <img v-if="item.preview" :src="item.preview" alt="" />
            <span class="gw-video__poster-label">
              Ouvrir la vidéo
              <v-icon size="14" color="currentColor">
                {{ icons.mdiOpenInNew }}
              </v-icon>
            </span>
          </a>
        </div>

        <div v-if="video.state.analyzing" class="gw-video__state">
          <v-progress-circular indeterminate size="22" width="2" />
          <span>
            Transcription de la vidéo puis proposition d'angles… Comptez une
            trentaine de secondes.
          </span>
        </div>

        <rmr-notice
          v-if="video.state.error"
          variant="warning"
          class="mt-2"
          :text="video.state.error"
        />

        <template v-if="source.is_video && !video.state.analyzing">
          <div class="gw-video__controls">
            <rmr-toggle-card
              :value="video.state.split"
              label="Découper la vidéo en plusieurs angles"
              size="sm"
              :disabled="!video.canSplit.value"
              @input="video.state.split = $event"
            />
            <span v-if="!video.canSplit.value" class="gw-video__hint">
              Sans time codes, la vidéo donne un seul angle.
            </span>
            <v-spacer />
            <rmr-action-button
              label="Proposer d'autres angles"
              variant="ghost"
              size="sm"
              :loading="video.state.proposing"
              :disabled="!video.canPropose.value"
              @click="video.proposeMoreAngles"
            />
            <rmr-action-button
              label="Reproposer"
              :icon="icons.mdiRefresh"
              variant="utility"
              size="sm"
              :loading="video.state.proposing"
              :disabled="!video.canPropose.value"
              @click="video.proposeAngles()"
            />
          </div>

          <video-angle-list
            v-if="video.state.angles.length"
            :value="video.state.angles"
            @add="video.addAngle"
            @remove="video.removeAngle"
          />
        </template>
      </v-card-text>

      <v-card-actions class="gw-video__actions">
        <span v-if="video.sendProgress.value" class="gw-video__progress">
          {{ video.sendProgress.value }}
        </span>
        <v-spacer />
        <rmr-action-button
          label="Fermer"
          variant="ghost"
          size="sm"
          :disabled="video.busy.value"
          @click="video.close"
        />
        <rmr-action-button
          :label="submitLabel"
          :icon="icons.mdiCreation"
          variant="primary"
          size="sm"
          :loading="video.state.sending"
          :disabled="!video.canSubmit.value"
          @click="video.submit"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { computed } from "@vue/composition-api";
import { mdiCreation, mdiOpenInNew, mdiRefresh } from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrNotice from "@/components/ui/feedback/RmrNotice.vue";
import RmrToggleCard from "@/components/ui/forms/RmrToggleCard.vue";
import VideoAngleList from "@/components/ai-factory/post-by-video/VideoAngleList.vue";

const PLATFORM_LABELS = {
  youtube: "Vidéo YouTube",
  instagram: "Vidéo Instagram",
  tiktok: "Vidéo TikTok",
  x: "Vidéo X",
  other: "Vidéo",
};

export default {
  name: "VideoAnglesDialog",
  components: {
    RmrActionButton,
    RmrNotice,
    RmrToggleCard,
    VideoAngleList,
  },
  props: {
    /** Le hook `useGlobalWatchVideo` de l'onglet (état + actions). */
    video: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    // =>  COMPUTED
    const item = computed(() => props.video.state.item);
    const source = computed(() => props.video.state.source || {});

    const platformLabel = computed(
      () => PLATFORM_LABELS[source.value.video_platform] || "Vidéo"
    );

    // La durée manque sur certaines plateformes (yt-dlp n'en remonte aucune
    // sur les réels Instagram) : on n'affiche rien plutôt qu'un « 0 s ».
    const durationLabel = computed(() => {
      const seconds = source.value.video_duration_s;
      if (!seconds) return "";
      const minutes = Math.floor(seconds / 60);
      const rest = seconds % 60;
      return minutes ? `${minutes} min ${rest} s` : `${rest} s`;
    });

    /** Seul YouTube s'embarque : ailleurs l'embed est reporté (T8). */
    const embedUrl = computed(() =>
      source.value.video_platform === "youtube" && source.value.video_id
        ? `https://www.youtube-nocookie.com/embed/${source.value.video_id}`
        : ""
    );

    const submitLabel = computed(() =>
      props.video.selectedAngles.value.length > 1
        ? "Générer les brouillons"
        : "Générer le brouillon"
    );

    // =>  DATA
    return {
      durationLabel,
      embedUrl,
      icons: { mdiCreation, mdiOpenInNew, mdiRefresh },
      item,
      platformLabel,
      source,
      submitLabel,
    };
  },
};
</script>

<style lang="scss" scoped>
.gw-video__head {
  display: block;
  padding-bottom: 8px;
}

.gw-video__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: $rmr-text-subtle;
}

.gw-video__title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  color: $rmr-text-base;
  word-break: break-word;
}

.gw-video__body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.gw-video__player {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  max-width: 100%;
  border-radius: $rmr-radius-lg;
  overflow: hidden;
  background: $rmr-surface-2;
}

.gw-video__frame {
  width: 100%;
  height: 100%;
  border: 0;
}

.gw-video__poster {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  text-decoration: none;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.gw-video__poster-label {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  border-radius: $rmr-radius-pill;
  background: rgba(15, 23, 42, 0.78);
  color: $rmr-surface-0;
  font-size: 12.5px;
  font-weight: 700;
}

.gw-video__state {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: $rmr-text-muted;
}

.gw-video__controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.gw-video__hint {
  font-size: 12px;
  color: $rmr-text-subtle;
}

.gw-video__actions {
  gap: 10px;
  padding: 12px 24px 18px;
}

.gw-video__progress {
  font-size: 12.5px;
  color: $rmr-text-muted;
}
</style>
